import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEffectivePermissions } from '../../../auth/useEffectivePermissions'
import { Button, Checkbox, DatePicker, EmptyState, Modal, Skeleton, TextInput, Toggle } from '../../../shared/ui'
import { notify } from '../../../store/notificationStore'
import { useBargeldlosListe, useSetBargeldlos, type BargeldlosAenderung, type BargeldlosSchule } from '../bargeldlosApi'
import { bargeldlosDefaultAb, bargeldlosErrorKey, bargeldlosStateText } from '../bargeldlos'
import { formatDateDE, isoToday } from '../office'

type Draft = BargeldlosAenderung & { errorKey?: string }

/** Unmount on close so reopening starts with fresh dates, filters and drafts. */
export function BargeldlosDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return open ? <BargeldlosDialogContent onClose={onClose} /> : null
}

function BargeldlosDialogContent({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('kasse')
  const { canWrite } = useEffectivePermissions()
  const allowed = canWrite('schulen')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState({ seite: 1, q: '', nurBargeldlos: false })
  // Keep unsaved rows when paging or searching, keyed by the canonical BIGINT string.
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const list = useBargeldlosListe(filter, allowed)
  const save = useSetBargeldlos()
  const searching = search.trim() !== filter.q
  const busy = save.isPending
  const pages = Math.max(1, Math.ceil((list.data?.gesamt ?? 0) / (list.data?.proSeite ?? 25)))

  useEffect(() => {
    const timer = window.setTimeout(() => setFilter(current => ({ ...current, q: search.trim(), seite: 1 })), 250)
    return () => window.clearTimeout(timer)
  }, [search])

  // Removing the last matching row can make the current filtered page disappear.
  useEffect(() => {
    if (list.data && !list.isFetching && filter.seite > pages) setFilter(current => ({ ...current, seite: pages }))
  }, [list.data, list.isFetching, filter.seite, pages])

  const initialDraft = (school: BargeldlosSchule): Draft => ({
    // The switch edits the scheduled state; the separate status line always describes today.
    bargeldlos: school.offenerZeitraum != null,
    ab: bargeldlosDefaultAb(),
  })
  const edit = (school: BargeldlosSchule, patch: Partial<BargeldlosAenderung>) => setDrafts(current => ({
    ...current, [school.schoolId]: { ...(current[school.schoolId] ?? initialDraft(school)), ...patch, errorKey: undefined },
  }))
  const submit = async (school: BargeldlosSchule) => {
    const draft = drafts[school.schoolId]
    if (!allowed || busy || !draft?.ab) return
    try {
      await save.mutateAsync({ schoolId: school.schoolId, bargeldlos: draft.bargeldlos, ab: draft.ab })
      setDrafts(current => {
        const next = { ...current }
        delete next[school.schoolId]
        return next
      })
      notify.success(t('bargeldlos.saved'), t('bargeldlos.savedDetail', {
        school: school.schulName, state: t(draft.bargeldlos ? 'bargeldlos.cashless' : 'bargeldlos.cash'), date: formatDateDE(draft.ab),
      }))
    } catch (error) {
      setDrafts(current => ({ ...current, [school.schoolId]: { ...draft, errorKey: bargeldlosErrorKey(error) } }))
    }
  }

  if (!allowed) return null
  return (
    <Modal open onClose={onClose} blocked={busy} title={t('bargeldlos.title')} subtitle={t('bargeldlos.hint')} size="lg"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-on-surface-variant" aria-live="polite">
            {list.data && t('bargeldlos.page', { page: list.data.seite, pages, count: list.data.gesamt })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <Button icon="chevron_left" disabled={busy || searching || list.isFetching || filter.seite <= 1}
              onClick={() => setFilter(current => ({ ...current, seite: current.seite - 1 }))}>{t('bargeldlos.previous')}</Button>
            <Button icon="chevron_right" disabled={busy || searching || list.isFetching || !list.data || filter.seite >= pages}
              onClick={() => setFilter(current => ({ ...current, seite: current.seite + 1 }))}>{t('bargeldlos.next')}</Button>
            <Button variant="ghost" disabled={busy} onClick={onClose}>{t('bargeldlos.close')}</Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-48 flex-1">
            <TextInput value={search} onChange={event => setSearch(event.target.value)} maxLength={80} disabled={busy}
              leadingIcon="search" placeholder={t('bargeldlos.search')} aria-label={t('bargeldlos.search')} />
          </div>
          <Checkbox checked={filter.nurBargeldlos} disabled={busy} label={t('bargeldlos.onlyCashless')} ariaLabel={t('bargeldlos.onlyCashless')}
            onChange={nurBargeldlos => setFilter(current => ({ ...current, nurBargeldlos, seite: 1 }))} />
        </div>
        {Object.keys(drafts).length > 0 && <p className="text-sm text-on-surface-variant">{t('bargeldlos.unsaved', { count: Object.keys(drafts).length })}</p>}
        {list.isError && (
          <div className="space-y-2" role="alert">
            <p className="text-sm text-error">{t(bargeldlosErrorKey(list.error))}</p>
            <Button icon="refresh" loading={list.isFetching} onClick={() => void list.refetch()}>{t('bargeldlos.retry')}</Button>
          </div>
        )}
        {list.isLoading || searching ? <Skeleton className="h-48 w-full" label={t('bargeldlos.loading')} /> : !list.isError && list.data && (
          list.data.zeilen.length === 0
            ? <EmptyState icon="search_off" title={t('bargeldlos.empty')} description={t('bargeldlos.emptyHint')} />
            : <ul className="divide-y divide-outline-variant/30" aria-label={t('bargeldlos.schools')} aria-busy={list.isFetching}>
              {list.data.zeilen.map(school => {
                const draft = drafts[school.schoolId]
                const value = draft ?? initialDraft(school)
                const scheduledStart = school.offenerZeitraum?.ab
                return (
                  <li key={school.schoolId} className="py-4 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-semibold text-on-surface break-words">{school.schulName}</h3>
                        <p className="text-sm text-on-surface-variant">{bargeldlosStateText(school, {
                          cash: t('bargeldlos.cash'), cashless: t('bargeldlos.cashless'), since: date => t('bargeldlos.since', { date }),
                        })}</p>
                        {scheduledStart && scheduledStart > isoToday() && <p className="text-xs text-on-surface-variant mt-1">{t('bargeldlos.scheduled', { date: formatDateDE(scheduledStart) })}</p>}
                        {school.bargeldlos && !scheduledStart && <p className="text-xs text-on-surface-variant mt-1">{t('bargeldlos.scheduledCash')}</p>}
                      </div>
                      <div className="flex flex-wrap items-end gap-3 sm:shrink-0">
                        <div className="self-center">
                          <Toggle checked={value.bargeldlos} disabled={busy || list.isFetching} label={t('bargeldlos.switch')}
                            ariaLabel={t('bargeldlos.switchFor', { school: school.schulName })} onChange={bargeldlos => edit(school, { bargeldlos })} />
                        </div>
                        <DatePicker value={value.ab} onChange={ab => edit(school, { ab })} label={t('bargeldlos.from')}
                          ariaLabel={t('bargeldlos.dateFor', { school: school.schulName })} placeholder={t('bargeldlos.chooseDate')}
                          clearLabel={t('bargeldlos.clearDate')} disabled={busy || list.isFetching} size="compact" className="w-36"
                          usePortal menuZIndex={100001} />
                        <Button variant="primary" icon="save" disabled={!draft || !value.ab || busy || list.isFetching}
                          loading={busy && save.variables?.schoolId === school.schoolId}
                          aria-label={t('bargeldlos.saveFor', { school: school.schulName })} onClick={() => void submit(school)}>{t('bargeldlos.save')}</Button>
                      </div>
                    </div>
                    {draft?.errorKey && <p role="alert" className="text-sm text-error">{t(draft.errorKey)}</p>}
                  </li>
                )
              })}
            </ul>
        )}
      </div>
    </Modal>
  )
}
